import { faFileUpload } from '@fortawesome/pro-regular-svg-icons/faFileUpload';
import { faImage } from '@fortawesome/pro-regular-svg-icons/faImage';
import { faVideo } from '@fortawesome/pro-regular-svg-icons/faVideo';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinearProgress } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import { PortraitPicture, SquarePicture, WidePicture } from 'assets/appConfig';
import { AppImages } from 'assets/appResources';
import ButtonCancel from 'components/Buttons/ButtonCancel';
import ImageSelectionDialog, { ImageSelectionResult } from 'components/ImageHandler/ImageSelectionDialog';
import MuiRegularButton from 'components/MuiExtras/Button/MuiRegularButton';
import MuiConfirmation from 'components/MuiExtras/MuiConfirmation';
import { withSnackbar, WithSnackbar } from 'components/SnackbarProvider/Snackbar';
import { SyncIndicator } from 'components/SyncIndicator';
import { AsyncUploader } from "lib/AsyncUploader";
import MediaUtils from 'lib/MediaUtils';
import { AspectRatio } from 'models/ApiEnums';
import { AsyncUploadModel, ContentMetadata, FileGetDto, FileThumbnaildGetDto } from 'models/ApiModels';
import { AttachType } from 'models/AppModels';
import * as React from 'react';
import Dropzone from 'react-dropzone';
import { WithTranslation, withTranslation } from 'react-i18next';

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export type UploadingStatus = "none" | "uploading" | "uploaded" | "canceled";
export type UploadStatusChange = (status: UploadingStatus) => void;
export type UploadCallback = (data: AsyncUploadModel) => void;
export type UploadRemoveCallback = (data: FileThumbnaildGetDto | ContentMetadata) => void;

export function ConvertUploadToFileThumbnaildGetDto(upload: AsyncUploadModel): FileThumbnaildGetDto {
    const newFile: FileGetDto = {
        fileId: upload.mainFile.fileId,
        mimeType: upload.mainFile.mimeType,
        url: upload.mainFile.tempUrl,
        metadata: upload.mainFile.metadata,
        mediaAsset: null,
    };

    const newThumb: FileGetDto =
        upload.thumbnail == null
            ? null
            : {
                fileId: upload.thumbnail.fileId,
                mimeType: upload.thumbnail.mimeType,
                url: upload.thumbnail.tempUrl,
                metadata: upload.thumbnail.metadata,
                mediaAsset: null,
            };

    return { mainFile: newFile, thumbnail: newThumb, resizedImage: null };
}


//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const styles = (theme: Theme) => createStyles({

    dropzone: {
        margin: "0 auto",
        height: 105,
        "& > input[type=file]": {
            display: "none"
        },
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center"
    },

    progressBar: {
        width: "100%"
    },

    imageContainer: {
        "overflow-y": "hidden",
        display: "flex",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: "center",
        width: "100%"
    },

    imageItem: {
        alignSelf: "center",
        padding: "4px",
        "& img": {
            height: "inherit",
            width: "inherit",
        }
    },

    thumbBox: {
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: theme.palette.grey["500"],
        padding: 4,
        textAlign: "center",
    }
});

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    dialog: any;
    isLoading: boolean;
    progressBar?: number;
    lastFileUploaded?: AsyncUploadModel;
}

interface IProps {
    acceptedTypes: AttachType[];
    aspectRatio: AspectRatio;
    attach?: FileThumbnaildGetDto[] | ContentMetadata;
    dropZoneText?: string;
    readOnly?: boolean;
    onUploadComplete?: UploadCallback;
    onUploadRemove?: UploadRemoveCallback;
    onUploadStatusChange?: UploadStatusChange;
}

type Props = IProps & WithStyles<typeof styles> & WithSnackbar & WithTranslation;

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

class AttachSelection extends React.Component<Props, IState> {

    private asyncUploader: AsyncUploader;
    private fileInputRef: React.RefObject<HTMLInputElement>;

    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null,
            isLoading: false,
            lastFileUploaded: null,
            progressBar: null
        };

        this.asyncUploader = new
            AsyncUploader({
                onFileUploadProgress: this.loadAttachProgress,
                onFileUploadSuccess: this.loadAttachSuccess,
                onFileUploadError: this.loadAttachError,
                onFileStarted: this.loadAttachStarted,
            });

    }

    private getDimensions() {
        if (this.props.aspectRatio == AspectRatio.Portrait) return PortraitPicture;
        if (this.props.aspectRatio == AspectRatio.Square) return SquarePicture;
        return WidePicture;
    }

    private loadImage = (image: ImageSelectionResult) => {

        if (image == null) {
            this.setState({ dialog: null });
        }
        else {

            this.setState({ isLoading: true, dialog: null, }, () => {

                this.asyncUploader.uploadImage(
                    image.editedImage || image.source,
                    image.editedImage ? null : this.props.aspectRatio
                );
            });

        }
    };

    private handleImageSelection(initialFile: File = null) {
        var d = this.getDimensions();
        this.setState({
            dialog: (
                <ImageSelectionDialog
                    acceptedFiles={MediaUtils.AcceptImages()}
                    initialSource="library"
                    initialFile={initialFile}
                    allowEdit
                    dimensions={
                        {
                            height: d.height,
                            width: d.width,
                        }
                    }
                    onClose={this.loadImage}
                />
            )
        });
    }


    private loadAttachStarted = () => {
        if (this.props.onUploadStatusChange) this.props.onUploadStatusChange("uploading");
    };

    private loadAttachProgress = (progress: number) => {
        this.setState({ progressBar: progress });
    };

    private loadAttachSuccess = (data: AsyncUploadModel) => {
        if (this.props.onUploadStatusChange) this.props.onUploadStatusChange("uploaded");

        if (data.success) {
            this.setState({ progressBar: null, lastFileUploaded: data, isLoading: false });
            if (this.props.onUploadComplete) this.props.onUploadComplete(data);
        }
        else {
            this.setState({ progressBar: null, lastFileUploaded: null, isLoading: false });
        }
    };

    private loadAttachError = (thrown: any) => {
        if (this.props.onUploadStatusChange) this.props.onUploadStatusChange("canceled");

        if (this.asyncUploader.isCancel(thrown)) {
            this.props.snackbar.showMessage(this.props.t("Components.AttachSelection.Canceled"), "success");
        }
        else {
            this.props.snackbar.showMessage(this.props.t("Components.AttachSelection.UploadError"), "error");
        }
        this.setState({ progressBar: null, lastFileUploaded: null, isLoading: false });
    };

    private loadAttach(e: File) {
        if (e.type.indexOf("image") == 0) {
            this.handleImageSelection(e);
        }
        else {
            this.setState({ isLoading: true }, () => {
                const formData = new FormData();
                formData.append('file', e, e.name);
                formData.append('createThumbnailAspectRatio', AspectRatio[this.props.aspectRatio]);
                this.asyncUploader.uploadForm(formData);
            });
        }
    }

    private removeAttach(fileOrContent: FileThumbnaildGetDto | ContentMetadata) {

        if (!this.props.onUploadRemove) return;

        this.setState(
            {
                dialog: (
                    <MuiConfirmation
                        message={this.props.t("Components.AttachSelection.ConfirmDelete")}
                        onClose={
                            (result) => {

                                this.setState(
                                    { dialog: null },
                                    () => {

                                        if (result) {

                                            if ((fileOrContent as FileThumbnaildGetDto).mainFile) {
                                                const attach = fileOrContent as FileThumbnaildGetDto;

                                                var files = [attach.mainFile.fileId];
                                                if (attach.thumbnail != null) files.push(attach.thumbnail.fileId);

                                                this.asyncUploader.delete(files)
                                                    .then(() => {
                                                        this.props.onUploadRemove(attach);
                                                    });
                                            }
                                            else {
                                                this.props.onUploadRemove(fileOrContent);
                                            }
                                        }

                                    }
                                );
                            }
                        }
                    />
                )
            }
        );
    }


    private handleAttachTypeButtonClick(acceptFiles: string[]) {
        this.fileInputRef.current.accept = acceptFiles?.join(",");
        this.fileInputRef.current.click();
    }


    private renderFileSelection() {
        const { acceptedTypes, attach, readOnly } = this.props;

        var files = attach as FileThumbnaildGetDto[];
        const selectedFileType = files && files.length > 0 ? MediaUtils.getAttachTypeFromMimeType(files[0].mainFile.mimeType) : null;

        return (
            <div >
                {
                    acceptedTypes.includes(AttachType.Video) && (selectedFileType == null || selectedFileType == AttachType.Video) &&
                    <MuiRegularButton
                        justIcon
                        link
                        simple
                        title={this.props.t("Common.AttachType.Video")}
                        disabled={selectedFileType != null || readOnly}
                        onClick={() => { this.handleAttachTypeButtonClick(MediaUtils.AcceptAzureVideos()); }}>
                        <FontAwesomeIcon icon={faVideo} fixedWidth />
                    </MuiRegularButton>
                }

                {
                    acceptedTypes.includes(AttachType.Image) && (selectedFileType == null || selectedFileType == AttachType.Image) &&
                    <MuiRegularButton
                        justIcon
                        link
                        simple
                        title={this.props.t("Common.AttachType.Image")}
                        disabled={selectedFileType != null || readOnly}
                        onClick={() => { this.handleImageSelection(); }}>
                        <FontAwesomeIcon icon={faImage} fixedWidth />
                    </MuiRegularButton>
                }

                {
                    acceptedTypes.includes(AttachType.File) && (selectedFileType == null || selectedFileType == AttachType.File) &&
                    <MuiRegularButton
                        justIcon
                        link
                        simple
                        title={this.props.t("Common.AttachType.File")}
                        disabled={selectedFileType != null || readOnly}
                        onClick={() => { this.handleAttachTypeButtonClick(MediaUtils.AcceptFiles()); }}>
                        <FontAwesomeIcon icon={faFileUpload} fixedWidth />
                    </MuiRegularButton>
                }

                {
                    selectedFileType != null && attach != null &&
                    (
                        <MuiRegularButton
                            justIcon
                            link
                            simple
                            size="small"
                            color="error"
                            variant="outlined"
                            title={this.props.t("Common.Buttons.DeleteText")}
                            disabled={readOnly}
                            onClick={() => {
                                var content = attach as ContentMetadata;
                                if (content.provider) this.removeAttach(content);
                                else this.removeAttach(attach[0] as FileThumbnaildGetDto);
                            }} >
                            <FontAwesomeIcon icon={faTrash} fixedWidth />
                        </MuiRegularButton>
                    )
                }

            </div>
        );
    }

    private renderAttach() {
        const { acceptedTypes, classes, attach, dropZoneText } = this.props;

        var d = this.getDimensions();

        var files = attach as FileThumbnaildGetDto[];

        var fileType = files == null || files.length == 0 ? null : MediaUtils.getAttachTypeFromMimeType(files[0].mainFile.mimeType);

        var imgUrl = files == null || files.length == 0
            ? null
            : files[0].thumbnail != null
                ? files[0].thumbnail.url
                : fileType == AttachType.Image
                    ? files[0].mainFile.url
                    : fileType == AttachType.Video
                        ? AppImages.VideoProcessing
                        : AppImages.DefaultDocumentThumbnail;


        var altText = files == null || files.length == 0
            ? null
            : files[0].thumbnail != null
                ? files[0].thumbnail.metadata.filename
                : fileType == AttachType.Image
                    ? files[0].mainFile.metadata.filename
                    : null;

        var bgImage =
            acceptedTypes.length == 1 && acceptedTypes.includes(AttachType.Image)
                ? AppImages.TakePicture
                : acceptedTypes.length == 1 && acceptedTypes.includes(AttachType.Video)
                    ? AppImages.TakeVideo
                    : AppImages.TakeUpload;

        var backgroundImageCss = { backgroundImage: `url("${bgImage}")` };

        return (
            <div style={{ width: "100%" }}>
                <div style={{ width: d.width + 20 }}>
                    <div className={classes.thumbBox}>

                        {
                            !this.state.isLoading && imgUrl == null
                                ? (
                                    <>
                                        <Dropzone
                                            multiple={false}
                                            onDropRejected={
                                                () => this.props.snackbar.showMessage(this.props.t("Components.AttachSelection.FileNotAccept"), "error")
                                            }
                                            onDropAccepted={(e) => {
                                                this.loadAttach(e[0]);
                                            }}
                                        >
                                            {
                                                ({ getRootProps, getInputProps, inputRef }) => {
                                                    this.fileInputRef = inputRef;
                                                    return (
                                                        <div
                                                            {...getRootProps()}
                                                            className={classes.dropzone}
                                                            style={backgroundImageCss}
                                                        >
                                                            <input {...getInputProps()} />

                                                        </div>
                                                    );
                                                }
                                            }
                                        </Dropzone>
                                        <span>{dropZoneText || this.props.t("Components.AttachSelection.DropzoneText")}</span>

                                    </>
                                )
                                : (
                                    <div className={classes.imageContainer} style={{ minHeight: d.height }}>
                                        <div className={classes.imageItem}>
                                            {
                                                this.state.isLoading
                                                    ? <SyncIndicator />
                                                    : <img alt={altText} src={imgUrl} style={{ maxWidth: d.width }} />
                                            }
                                        </div>
                                    </div>
                                )
                        }

                        {
                            this.state.progressBar != null
                                ? (
                                    <>
                                        <div className={classes.progressBar}>
                                            <LinearProgress variant="determinate" value={this.state.progressBar} />
                                        </div>
                                        <span>{this.state.progressBar + "%"}</span>
                                        <ButtonCancel
                                            link
                                            simple
                                            onClick={
                                                () => {
                                                    this.asyncUploader.cancel(this.props.t("Components.AttachSelection.Interrupted"));
                                                }
                                            }
                                        />
                                    </>
                                )
                                : this.renderFileSelection()
                        }
                    </div>
                </div>
            </div>
        );
    }



    public render() {
        const { dialog } = this.state;

        return (
            <>
                {dialog}

                {this.renderAttach()}
            </>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(styles)(AttachSelection)));